import { useState } from "react";
import ShareholderKYCForm from "./personal_kyc_form";
import { Spinner, Button } from "react-bootstrap";
import StatusTag from '@/components/Common/StatusTag';
import { STATUS_MAP } from "@/utils/constant";
import { useExpandedCompanies } from "../shareholders/use_expanded_companies";
import "../shareholders/shareholders.css";

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("") || "?";

const TypePill = ({ type }) =>
  type === "BUSINESS" ? (
    <span className="sh-pill sh-pill-business">Business</span>
  ) : (
    <span className="sh-pill sh-pill-individual">Individual</span>
  );

const BeneficiaryPill = ({ isOwner }) =>
  isOwner ? <span className="sh-pill sh-pill-yes">Yes</span> : <span className="sh-pill sh-pill-no">No</span>;

const ShareholdersKyc = ({ documentsList, reloadDocumentsList, goToNextStep, reloadPersonalKyc, shareholdersWithKyc, businessKyc }) => {
  // Every shareholder is listed so the declared structure stays visible, but only
  // the ones the backend flags need KYC; the rest are shown as not required.
  const kycSubjects = shareholdersWithKyc.filter((s) => s.kyc_required);
  // An individual may hold shares in a business shareholder rather than in the
  // borrower company, so each company's owners are listed beneath it.
  const borrowerRows = shareholdersWithKyc.filter((s) => !s.business_shareholder);
  const ownersOf = (company) =>
    shareholdersWithKyc.filter((s) => s.business_shareholder === company.uuid);
  // A holding whose company is not itself listed would otherwise disappear.
  const ungrouped = shareholdersWithKyc.filter(
    (s) =>
      s.business_shareholder &&
      !borrowerRows.some((row) => row.uuid === s.business_shareholder)
  );
  const companyRows = borrowerRows.filter((s) => ownersOf(s).length > 0);
  const plainRows = [...borrowerRows.filter((s) => ownersOf(s).length === 0), ...ungrouped];

  const [loading] = useState(false);
  const [selectedShareholder, setSelectedShareholder] = useState(null);
  const { isExpanded, toggleCompany } = useExpandedCompanies();
  const kycComplete = kycSubjects.every(s => ['completed', 'verified'].includes(s.kyc?.status));

  const afterUpdateKyc = async () => {
    setSelectedShareholder(null); // return to list view
  };

  const kycActionCell = (s) => (
    <div className="sh-actions" onClick={(event) => event.stopPropagation()}>
      {s.kyc_required ? (
        <Button
          className="sh-kyc-action"
          size="sm"
          variant="primary"
          onClick={() => setSelectedShareholder(s)}
          data-testid={
            s.shareholder_type === "BUSINESS"
              ? "fincobox-kyc-shareholder-business-update"
              : "fincobox-kyc-shareholder-individual-update"
          }
        >
          {!s.kyc?.status || ["pending", "input_required", "rejected"].includes(s.kyc.status) ? "Update" : "View"}
        </Button>
      ) : (
        <span className="text-muted small">Not required</span>
      )}
    </div>
  );

  const nameCell = (name, isBusiness) => (
    <div className="sh-name-cell">
      <span className={`sh-avatar ${isBusiness ? "sh-avatar-business" : ""}`}>{initials(name)}</span>
      <span className={isBusiness ? "text-capitalize" : "text-capitalize text-muted"}>{name}</span>
    </div>
  );

  const ownerRow = (owner, companyName) => (
    <tr key={owner.uuid}>
      <td>{nameCell(owner.name, false)}</td>
      <td className="text-end sh-pct">
        {owner.shareholding_percentage}% of {companyName}
      </td>
      <td>
        <TypePill type={owner.shareholder_type} />
      </td>
      <td>
        {owner.kyc_required ? (
          <StatusTag
            status={owner?.kyc?.status || "pending"}
            stepComplete={false}
            label={STATUS_MAP[owner.kyc?.status]?.label || "Pending"}
          />
        ) : (
          <span className="text-muted small">Not required</span>
        )}
      </td>
      <td>
        <BeneficiaryPill isOwner={owner.is_business_owner} />
      </td>
      <td>{kycActionCell(owner)}</td>
    </tr>
  );

  const plainRow = (s) => (
    <tr key={s.uuid}>
      <td>{nameCell(s.name, s.shareholder_type === "BUSINESS")}</td>
      <td className="text-end sh-pct">{s.shareholding_percentage}%</td>
      <td>
        <TypePill type={s.shareholder_type} />
      </td>
      <td>
        {s.kyc_required ? (
          <StatusTag
            status={s?.kyc?.status || "pending"}
            stepComplete={false}
            label={STATUS_MAP[s.kyc?.status]?.label || "Pending"}
          />
        ) : (
          <span className="text-muted small">Not required</span>
        )}
      </td>
      <td>
        <BeneficiaryPill isOwner={s.is_business_owner} />
      </td>
      <td>{kycActionCell(s)}</td>
    </tr>
  );

  const renderList = () => (
    <div className="sh-wrap">
      {shareholdersWithKyc.length === 0 ? (
        <p className="text-muted mb-0">No shareholders added yet.</p>
      ) : (
        <>
          {/* Business shareholders with their own declared owners, each as a
              card so the company and the people who own it read as one unit. */}
          {companyRows.map((s) => {
            const owners = ownersOf(s);
            const expanded = isExpanded(s.uuid);
            return (
              <div className="sh-card" key={s.uuid}>
                <div
                  className="sh-card-header d-flex align-items-center flex-wrap gap-2"
                  role="button"
                  tabIndex={0}
                  aria-expanded={expanded}
                  onClick={() => toggleCompany(s.uuid)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggleCompany(s.uuid);
                    }
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <span className="sh-chevron">{expanded ? "▾" : "▸"}</span>
                  <span className="sh-avatar sh-avatar-business">{initials(s.name)}</span>
                  <b className="text-capitalize">{s.name}</b>
                  <TypePill type="BUSINESS" />
                  <span className="text-muted small">{s.shareholding_percentage}% of borrower</span>
                  <div className="d-flex align-items-center gap-2 ms-auto">
                    {s.kyc_required ? (
                      <StatusTag
                        status={s?.kyc?.status || "pending"}
                        stepComplete={false}
                        label={STATUS_MAP[s.kyc?.status]?.label || "Pending"}
                      />
                    ) : (
                      <span className="text-muted small">Not required</span>
                    )}
                  </div>
                  {kycActionCell(s)}
                </div>
                {expanded && (
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="sh-table">
                        <thead>
                          <tr>
                            <th>Name</th>
                            <th className="text-end">Share of {s.name}</th>
                            <th>Type</th>
                            <th>KYC Status</th>
                            <th>Beneficiary Owner</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>{owners.map((owner) => ownerRow(owner, s.name))}</tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Individuals, and companies with no declared owners of their own */}
          {plainRows.length > 0 && (
            <div className="sh-plain-card">
              <div className="table-responsive">
                <table className="sh-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="text-end">Shareholding</th>
                      <th>Type</th>
                      <th>KYC Status</th>
                      <th>Beneficiary Owner</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>{plainRows.map((s) => plainRow(s))}</tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className="_innerKyc_grid ing_exMarg" style={{ width: '100%' }}>
      <div className="_inKycHead">
        <h1>Shareholder KYC</h1>
        <p>Please fill KYC details and upload documents for below shareholders.</p>
      </div>

      {loading && (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
          <p>Loading shareholders...</p>
        </div>
      )}


      {!loading && (
        <div className="_inFr_flexBx anvBas_select w-full">
          {selectedShareholder ? (
            <ShareholderKYCForm
              documentsList={documentsList}
              reloadDocumentsList={reloadDocumentsList}
              businessKyc={businessKyc}
              reloadPersonalKyc={reloadPersonalKyc}
              shareholder={selectedShareholder}
              afterSubmit={afterUpdateKyc}
              onCancel={() => setSelectedShareholder(null)}
            />
          ) : (
            <>
              {renderList()}
              <div className="d-flex justify-content-start mt-4">
                <Button
                  id="b_submit_personal_kyc_page"
                  variant="primary"
                  disabled={!kycComplete}
                  onClick={goToNextStep}
                  style={{ minWidth: '240px' }}
                  data-testid="fincobox-kyc-shareholders-submit"
                >
                  Submit
                </Button>
              </div>
            </>

          )}
        </div>
      )}
    </div>
  );
};

export default ShareholdersKyc;
